import { TrashIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { useEffect, useRef, useState } from 'react'
import { getServicingNotes, postServicingNotes } from 'services'
import { confirm, formatDateYMD, InputValidate, openAuditLog } from 'utils'
import { RenderInput } from 'utils/RenderInput'

type Kind = 'noticeOfDefault' | 'extension'

const extensionSectionInput = {
  inputType: 'section',
  title: 'Extension Information',
  span: 'full',
}

/**
 * Servicing-note fields that used to sit on Servicing Information → Default & Extensions.
 * Values stay on the servicing note; this only renders them on Default Tracking or Loan Extensions.
 */
export const ServicingNoteFields = ({ kind, readOnly = false }: { kind: Kind; readOnly?: boolean }) => {
  const [inputs, setInputs] = useState<Record<string, any>>({})
  const [addonInputs, setAddonInputs] = useState<Record<string, any>>({})
  const [data, setData] = useState<Record<string, any>>({})
  const dataRef = useRef<Record<string, any>>({})
  const [orgData, setOrgData] = useState<Record<string, any>>({})

  useEffect(() => {
    let cancelled = false
    getServicingNotes()
      .then((res: any) => {
        if (cancelled || !res?.success) return
        const defs =
          kind === 'extension'
            ? { extensionSection: extensionSectionInput, ...(res.extensionInputs || {}) }
            : res.noticeOfDefaultInputs || {}
        setInputs(defs)
        setAddonInputs(res.extensionInputs || {})
        setData(res.data || {})
        dataRef.current = res.data || {}
        setOrgData(res.data || {})
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [kind])

  const save = async (key: string, value: any) => {
    if (readOnly) return
    await postServicingNotes({ key, value })
    setOrgData((prev) => ({ ...prev, [key]: value }))
  }

  const onChange = (key: string, value: any) => {
    if (readOnly) return
    const next = { ...dataRef.current, [key]: value }
    dataRef.current = next
    setData(next)
  }

  const onBlur = async (key: string) => {
    if (readOnly) return
    const value = dataRef.current[key]
    if (
      (typeof value === 'string' && value === orgData[key]) ||
      (typeof value === 'object' && JSON.stringify(value) === JSON.stringify(orgData[key]))
    )
      return
    await save(key, value)
  }

  const showHistory = (key: string, fieldLabel?: string) => {
    if (readOnly) return
    openAuditLog({
      table: 'Servicing Information',
      field: key,
      fieldLabel,
      keys: { field: key },
      renderValue: (value: any) => <span dangerouslySetInnerHTML={{ __html: value }} />,
    })
  }

  const setAdditional = (rows: any[]) => {
    const next = { ...dataRef.current, additionalExtension: rows }
    dataRef.current = next
    setData(next)
    return rows
  }

  const onAddExtension = () => {
    if (readOnly) return
    const rows = setAdditional([{ id: Date.now() }, ...(dataRef.current.additionalExtension || [])])
    save('additionalExtension', rows)
  }

  const onRemoveExtension = async (index: number) => {
    if (readOnly) return
    const isContinue = await confirm('Are you sure you want to remove this additional Extension Information?')
    if (!isContinue) return
    const rows = [...(dataRef.current.additionalExtension || [])]
    rows.splice(index, 1)
    setAdditional(rows)
    save('additionalExtension', rows)
  }

  const onSubChange = (index: number, subKey: string, value: any) => {
    if (readOnly) return
    const rows = cloneDeep(dataRef.current.additionalExtension || [])
    if (!rows[index]) return
    rows[index][subKey] = value
    setAdditional(rows)
  }

  const onSubBlur = () => {
    if (readOnly) return
    save('additionalExtension', dataRef.current.additionalExtension || [])
  }

  if (!Object.keys(inputs).length) return null

  const spanClass = (input: any) =>
    input.span === 'full' || input.inputType === 'section' ? 'col-span-full' : `col-span-${input.span || 4}`

  const renderField = (key: string, input: any, value: any) => {
    const next = { ...input }
    next.value = value ?? ''
    if (next.type === 'date' && next.value?.length > 0) next.value = formatDateYMD(next.value)
    next.history = !readOnly
    next.disabled = readOnly
    next.readOnly = readOnly
    next.error = InputValidate(next)
    if (key === 'extensionSection' && !readOnly) {
      next.additionalElements = (
        <div className="flex justify-end">
          <span className="text-shade-blue cursor-pointer hover:underline" onClick={onAddExtension}>
            + Add
          </span>
        </div>
      )
    }
    return next
  }

  return (
    <div className="grid grid-cols-12 gap-2 mb-4">
      {Object.keys(inputs).map((key) => {
        const input = renderField(key, inputs[key], data[key])
        return (
          <div key={key} className={spanClass(input)}>
            <RenderInput
              input={input}
              Key={key}
              onChange={onChange}
              onBlur={() => onBlur(key)}
              showHistory={readOnly ? undefined : () => showHistory(key, input.title)}
            />
          </div>
        )
      })}
      {kind === 'extension' &&
        (data.additionalExtension || []).map((item: any, index: number) => {
          const subInputs = cloneDeep(addonInputs)
          return (
            <div key={item.id || index} className="col-span-full grid grid-cols-12 gap-2">
              <div className="col-span-full flex justify-end gap-2 pb-2 items-center border-b border-gray-300">
                <RenderInput
                  input={{ inputType: 'toggle', title: 'Active', value: item.isActive, disabled: readOnly, readOnly }}
                  Key={`additionalExtension-isActive-${index}`}
                  onChange={(_key: string, value: any) => onSubChange(index, 'isActive', value)}
                  onBlur={onSubBlur}
                />
                <RenderInput
                  input={{ inputType: 'toggle', title: 'Hide', value: item.isHidden, disabled: readOnly, readOnly }}
                  Key={`additionalExtension-isHidden-${index}`}
                  onChange={(_key: string, value: any) => onSubChange(index, 'isHidden', value)}
                  onBlur={onSubBlur}
                />
                {!readOnly && (
                  <span
                    className="text-red-500 flex items-center gap-2 cursor-pointer hover:underline"
                    onClick={() => onRemoveExtension(index)}
                  >
                    <TrashIcon className="w-4 h-4" />
                    Delete
                  </span>
                )}
              </div>
              {!item.isHidden &&
                Object.keys(subInputs).map((subKey) => {
                  const subInput = renderField(subKey, subInputs[subKey], item[subKey])
                  return (
                    <div key={`${item.id}-${subKey}`} className={`${spanClass(subInput)} ${!item.isActive ? 'opacity-50' : ''}`}>
                      <RenderInput
                        input={subInput}
                        Key={`additionalExtension-${item.id}-${subKey}`}
                        onChange={(_key: string, value: any) => onSubChange(index, subKey, value)}
                        onBlur={onSubBlur}
                        showHistory={
                          readOnly
                            ? undefined
                            : () => showHistory(`additionalExtension-${item.id}-${subKey}`, `${subInput.title} - ${index + 1}`)
                        }
                      />
                    </div>
                  )
                })}
            </div>
          )
        })}
    </div>
  )
}
